/**
 * Unwrapper for the Onboarding Program APIs (and future new-convention APIs).
 *
 * The handover doc specifies: `generalProcessingStatus` as an explicit
 * "SUCCESS" | "FAILED" STRING, `contents` as a single object, and
 * `errorInformation` as a single object ({errorCode, errorMessage}).
 *
 * The backend as SHIPPED reuses the legacy response builders instead:
 *   generalProcessingStatus: { processingStatus: "SUCCESS", processingDetails }
 *   contents: [ { ...row } ]            // array-wrapped single object
 * ...so this helper tolerates BOTH shapes. If the backend is later aligned to
 * the doc, nothing here needs to change — the strict shape is checked first.
 *
 * Older APIs keep using ApiErrorHelper.extractApiErrorMessage. Do not merge.
 */

export class ApiEnvelopeError extends Error {
  errorCode: string;
  /** Raw errorInformation[0].additionalDetails, when the backend sends it —
   *  e.g. dine-in's INVALID_OR_MISSING_SESSION_CODE includes hostName/userId
   *  alongside the error code. Optional since most error responses don't. */
  additionalDetails?: Record<string, unknown>;

  constructor(
    errorCode: string,
    message: string,
    additionalDetails?: Record<string, unknown>
  ) {
    super(message);
    this.name = "ApiEnvelopeError";
    this.errorCode = errorCode;
    this.additionalDetails = additionalDetails;
  }
}

/**
 * True for "SUCCESS" (doc shape), {processingStatus:"SUCCESS"|"OK"} (legacy
 * shape), or {code:"OK"|"SUCCESS"} (the business-subscriptions shape - see the
 * `status` branch in unwrapEnvelope).
 */
function isSuccessStatus(status: unknown): boolean {
  if (status === "SUCCESS" || status === "OK") return true;
  if (status && typeof status === "object") {
    const rec = status as Record<string, unknown>;
    const ps = rec.processingStatus;
    if (ps === "SUCCESS" || ps === "OK") return true;
    const code = rec.code;
    return code === "SUCCESS" || code === "OK";
  }
  return false;
}

/** Reads {errorCode, errorMessage, additionalDetails} from an object (doc)
 *  or array (legacy) errorInformation - or from the `errors` array used by the
 *  business-subscriptions shape, whose entries carry {code, message}. */
function extractErrorInfo(errorInformation: unknown): {
  errorCode?: string;
  errorMessage?: string;
  additionalDetails?: Record<string, unknown>;
} {
  const info = Array.isArray(errorInformation)
    ? errorInformation[0]
    : errorInformation;
  if (!info || typeof info !== "object") return {};
  const rec = info as Record<string, unknown>;
  return {
    errorCode:
      typeof rec.errorCode === "string"
        ? rec.errorCode
        : typeof rec.code === "string" // business-subscriptions `errors` entry
          ? rec.code
          : undefined,
    errorMessage:
      typeof rec.errorMessage === "string"
        ? rec.errorMessage
        : typeof rec.errorDetail === "string" // legacy field name
          ? rec.errorDetail
          : typeof rec.message === "string" // business-subscriptions
            ? rec.message
            : undefined,
    additionalDetails:
      rec.additionalDetails && typeof rec.additionalDetails === "object"
        ? (rec.additionalDetails as Record<string, unknown>)
        : undefined,
  };
}

/**
 * If the backend array-wrapped a single object (legacy `contents: [ {...} ]`),
 * unwrap it. Doc-shaped object contents pass through untouched.
 */
export function coerceSingle(contents: unknown): Record<string, unknown> {
  if (Array.isArray(contents)) {
    const first = contents[0];
    return first && typeof first === "object"
      ? (first as Record<string, unknown>)
      : {};
  }
  return contents && typeof contents === "object"
    ? (contents as Record<string, unknown>)
    : {};
}

/**
 * List-shaped complement to coerceSingle, for endpoints whose `contents` is a
 * genuine collection rather than an array-wrapped single object (e.g. dining's
 * GET /{businessId}/dine-in-sessions, which returns every live table).
 *
 * coerceSingle would silently return only the FIRST element of such a
 * response, so the two are deliberately separate calls — pick based on what
 * the endpoint means, not on what the JSON happens to look like.
 *
 * Tolerant in the same spirit as the rest of this module: a lone object is
 * treated as a one-element list, null/undefined as empty, and non-object
 * entries (nulls, stray primitives) are dropped rather than handed downstream
 * for a converter to crash on.
 */
export function coerceList(contents: unknown): Record<string, unknown>[] {
  if (Array.isArray(contents)) {
    return contents.filter(
      (entry): entry is Record<string, unknown> => !!entry && typeof entry === "object"
    );
  }
  if (contents && typeof contents === "object") {
    return [contents as Record<string, unknown>];
  }
  return [];
}

/**
 * Parses a fetch Response and returns `contents` (shape-tolerant, see header).
 * Throws ApiEnvelopeError (with the backend errorCode when present) on
 * failure — callers/thunks map codes to friendly copy.
 */
export async function unwrapEnvelope<T>(
  response: Response,
  fallbackMessage = "Something went wrong. Please try again."
): Promise<T> {
  const data = (await response.json().catch(() => null)) as Record<
    string,
    unknown
  > | null;

  if (data && isSuccessStatus(data.generalProcessingStatus)) {
    return data.contents as T;
  }

  // Third observed envelope (business-subscriptions service):
  //   { errors, status: { code: "OK", message: "SUCCESS" }, contents: [...] }
  // Checked AFTER generalProcessingStatus so every existing caller keeps hitting
  // its own branch first and nothing about their behaviour changes. Tolerating
  // it here rather than adding a second unwrapper: this is the third shape the
  // same house envelope has arrived in, and two unwrappers would only drift.
  if (data && isSuccessStatus(data.status)) {
    return data.contents as T;
  }

  const { errorCode, errorMessage, additionalDetails } = extractErrorInfo(
    data?.errorInformation ?? data?.errors ?? data?.status
  );
  throw new ApiEnvelopeError(
    errorCode ?? `HTTP_${response.status}`,
    errorMessage ?? fallbackMessage,
    additionalDetails
  );
}

export default { unwrapEnvelope, coerceSingle, coerceList, ApiEnvelopeError };
